import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Divider,
  Menu,
  MenuItem,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useNavigate } from 'react-router-dom';
import SuccessMessage from '../../../components/successMessage';
import ErrorMessage from '../../../components/errorMessage';
import Loading from '../../../components/loading';
import MedicineFormModal from '../medicineFormModal';
import { useMedicines } from '../../../hooks/useMedicines';
import { useDeleteMedicine } from '../hooks/useDeleteMedicine';

function MedicineListWeb() {
  const navigate = useNavigate();

  const { medicines, isLoading, error: listError, refetch: getMedicines } = useMedicines();
  const { remove } = useDeleteMedicine();

  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (listError) {
      setShowErrorMessage(true);
      setErrorMessage(listError);
    }
  }, [listError]);

  const [isModalOpen, setModalOpen] = useState(false);
  const [isConfirmModalOpen, setConfirmModalOpen] = useState(false);
  const [selectedMedicine, setSelectedMedicine] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedMedicineId, setSelectedMedicineId] = useState(null);
  const isMenuOpen = Boolean(anchorEl);

  const clickMenu = (event, medicineId) => {
    setAnchorEl(event.currentTarget);
    setSelectedMedicineId(medicineId);
  };

  const closeMenu = () => setAnchorEl(null);

  const openCreateModal = () => {
    setSelectedMedicine(null);
    setModalOpen(true);
  };

  const openEditModal = () => {
    const medicine = medicines.find((m) => m.id === selectedMedicineId);
    if (medicine) {
      setSelectedMedicine(medicine);
      setModalOpen(true);
    }
    closeMenu();
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedMedicine(null);
  };

  const openConfirmModal = () => {
    setAnchorEl(null);
    setConfirmModalOpen(true);
  };

  const closeConfirmModal = () => {
    setConfirmModalOpen(false);
    setSelectedMedicineId(null);
  };

  const handleMedicineSaved = () => {
    getMedicines();
    setSuccessMessage('Medicamento salvo com sucesso.');
    setShowSuccessMessage(true);
  };

  const confirmDelete = () => {
    setConfirmModalOpen(false);
    remove(selectedMedicineId)
      .then((res) => {
        getMedicines();
        setSuccessMessage(res.data ?? 'Medicamento excluído com sucesso.');
        setShowSuccessMessage(true);
      })
      .catch((err) => {
        setShowErrorMessage(true);
        setErrorMessage(err.response?.data ?? 'Erro ao excluir medicamento.');
        if (err.response?.status === 401) navigate('/expired-session');
      })
      .finally(() => {
        setSelectedMedicineId(null);
      });
  };

  return (
    <>
      <Loading isToHidden={isLoading === false} />
      {showSuccessMessage && (
        <SuccessMessage
          message={successMessage}
          show={showSuccessMessage}
          setShow={setShowSuccessMessage}
        />
      )}
      {showErrorMessage && (
        <ErrorMessage
          message={errorMessage}
          show={showErrorMessage}
          setShow={setShowErrorMessage}
        />
      )}

      <Box sx={{ p: 4 }}>
        <Container maxWidth="lg">
          <Paper elevation={0} variant="outlined" sx={{ p: 4, borderRadius: '12px' }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 3,
              }}
            >
              <Typography variant="h5" component="h1" sx={{ fontWeight: '600' }}>
                Medicamentos
              </Typography>
              <Button variant="outlined" onClick={openCreateModal}>
                Adicionar Medicamento
              </Button>
            </Box>

            <List>
              {medicines.length === 0 && !isLoading ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ py: 2, textAlign: 'center' }}
                >
                  Nenhum medicamento cadastrado
                </Typography>
              ) : (
                medicines.map((medicine) => (
                  <React.Fragment key={medicine.id}>
                    <ListItem
                      secondaryAction={
                        <IconButton onClick={(event) => clickMenu(event, medicine.id)}>
                          <MoreVertIcon />
                        </IconButton>
                      }
                    >
                      <ListItemText
                        primary={medicine.description}
                        secondary={medicine.controlled ? 'Controlado' : 'Não controlado'}
                        secondaryTypographyProps={{ fontSize: '0.7rem', color: 'text.disabled' }}
                      />
                    </ListItem>
                    <Divider component="li" />
                  </React.Fragment>
                ))
              )}
            </List>
          </Paper>
        </Container>

        <Menu anchorEl={anchorEl} open={isMenuOpen} onClose={closeMenu}>
          <MenuItem onClick={openEditModal}>Editar</MenuItem>
          <MenuItem onClick={openConfirmModal} sx={{ color: 'error.main' }}>
            Excluir
          </MenuItem>
        </Menu>

        <MedicineFormModal
          open={isModalOpen}
          onClose={closeModal}
          onSave={handleMedicineSaved}
          medicine={selectedMedicine}
        />

        <Dialog open={isConfirmModalOpen} onClose={closeConfirmModal}>
          <DialogTitle sx={{ fontWeight: 'bold' }}>Confirmar Exclusão</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Você tem certeza que deseja excluir este medicamento? Essa ação não pode ser desfeita.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeConfirmModal}>Cancelar</Button>
            <Button onClick={confirmDelete} variant="contained" color="error" autoFocus>
              Excluir
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
}

export default MedicineListWeb;
